import { Service } from '@toeverything/infra';

import { AIProvider } from '../../../blocksuite/presets/ai/provider';
import type { TranscriptChunkForUI } from '../../../hooks/transcription-chunk-pipeline';
import type { WorkspacePropertiesAdapter } from './adapter';
import {
  type SerializedSocialFeedItem,
  type SerializedSummaryChunk,
  type SerializedTranscriptionChunk,
  SummaryChunkSchema,
  TranscriptionChunkInputSchema,
} from './schema';
import type { TranscriptionBlobStorage } from './transcription-blob-storage';

export class TranscriptionService extends Service {
  constructor(
    private readonly blobStorage: TranscriptionBlobStorage,
    private readonly propertiesAdapter: WorkspacePropertiesAdapter
  ) {
    super();
  }

  async ensurePageLoaded(pageId: string): Promise<void> {
    return this.blobStorage.ensurePageLoaded(pageId);
  }

  getPageTranscription(pageId: string): SerializedTranscriptionChunk[] {
    return this.blobStorage.getPageTranscriptionSync(pageId);
  }

  setPageTranscription(
    pageId: string,
    chunks: SerializedTranscriptionChunk[]
  ): void {
    this.blobStorage.setPageTranscription(pageId, chunks);
  }

  getPageSummary(pageId: string): SerializedSummaryChunk[] {
    return this.blobStorage.getPageSummarySync(pageId);
  }

  setPageSummary(pageId: string, chunks: SerializedSummaryChunk[]): void {
    this.blobStorage.setPageSummary(pageId, chunks);
  }

  addPageTranscriptionChunk(
    pageId: string,
    chunk: Omit<SerializedTranscriptionChunk, 'id'>
  ): string {
    const chunkId = `chunk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const validated = TranscriptionChunkInputSchema.parse({ ...chunk });
    const fullChunk: SerializedTranscriptionChunk = {
      id: chunkId,
      ...validated,
    };

    this.blobStorage.addPageTranscriptionChunk(pageId, fullChunk);
    return chunkId;
  }

  removePageTranscriptionChunk(pageId: string, chunkId: string): boolean {
    return this.blobStorage.removePageTranscriptionChunk(pageId, chunkId);
  }

  updatePageTranscriptionChunk(
    pageId: string,
    chunkId: string,
    updates: Partial<SerializedTranscriptionChunk>
  ): boolean {
    return this.blobStorage.updatePageTranscriptionChunk(
      pageId,
      chunkId,
      updates
    );
  }

  clearPageTranscription(pageId: string): void {
    this.blobStorage.setPageTranscription(pageId, []);
  }

  // ---- social feed operations ----
  getPageSocialFeed(pageId: string): SerializedSocialFeedItem[] {
    return this.propertiesAdapter.getPageSocialFeed(
      pageId
    ) as unknown as SerializedSocialFeedItem[];
  }

  setPageSocialFeed(
    pageId: string,
    items: Array<{
      attendeeEmail: string;
      attendeeName?: string;
      text: string;
      confidence: number;
      source?: string;
      timestamp?: string;
    }>
  ): void {
    if (!Array.isArray(items)) return;
    let counter = 0;
    const toSave = items
      .filter(it => it.attendeeEmail && it.text)
      .map(it => ({
        id: `sf-${Date.now()}-${counter++}`,
        attendeeEmail: it.attendeeEmail,
        attendeeName: it.attendeeName ?? '',
        text: it.text,
        confidence: it.confidence,
        ...(it.source ? { source: it.source } : {}),
        ...(it.timestamp ? { timestamp: it.timestamp } : {}),
      }));
    this.propertiesAdapter.setPageSocialFeed(
      pageId,
      toSave as unknown as Parameters<
        typeof this.propertiesAdapter.setPageSocialFeed
      >[1]
    );
  }

  getPageTranscriptText(pageId: string): string {
    return this.getSortedChunks(pageId)
      .map(chunk => chunk.text)
      .join(' ');
  }

  getPageTranscriptForExport(pageId: string): string {
    const chunks = this.getSortedChunks(pageId);
    if (chunks.length === 0) return '';

    const lines: string[] = [];
    for (const chunk of chunks) {
      const timestamp = this.formatTimestampMs(chunk.timestampMs);
      const speaker = chunk.speaker || 'Speaker';
      lines.push(`${speaker}  ${timestamp}`);
      lines.push(chunk.text);
      lines.push('');
    }
    return lines.join('\n').trim();
  }

  editChunkByTextAndTimestamp(
    pageId: string,
    chunkText: string,
    chunkTimestampSec: number,
    newText: string
  ): boolean {
    const existingChunks = this.getPageTranscription(pageId);
    const targetMs = Math.floor(chunkTimestampSec * 1000);
    const chunkIndex = existingChunks.findIndex(
      chunk =>
        chunk.text === chunkText && Math.floor(chunk.timestampMs) === targetMs
    );

    if (chunkIndex !== -1) {
      const chunkToEdit = existingChunks[chunkIndex];
      if (!chunkToEdit) return false;
      const result = this.updatePageTranscriptionChunk(pageId, chunkToEdit.id, {
        text: newText,
      });
      return result;
    } else {
      return false;
    }
  }

  getPageTranscriptWithTimestamps(pageId: string): string {
    const chunks = this.getSortedChunks(pageId);
    if (chunks.length === 0) return '';
    return chunks
      .map(chunk => {
        const timestamp = this.formatTimestampMs(chunk.timestampMs);
        // Include speaker if available
        const prefix = chunk.speaker
          ? `[${timestamp}] ${chunk.speaker}: `
          : `[${timestamp}] `;
        return prefix + chunk.text;
      })
      .join('\n');
  }

  parseTimestampToMs(timestampStr: string): number {
    let seconds: number;
    if (timestampStr.includes(':')) {
      const parts = timestampStr.split(':');
      const minutesPart = parts[0] ?? '0';
      const secondsPart = parts[1] ?? '0';
      const secondsValue = parseFloat(secondsPart);
      seconds = parseInt(minutesPart, 10) * 60 + secondsValue;
    } else {
      seconds = parseFloat(timestampStr);
    }
    if (!Number.isFinite(seconds)) return 0;
    return Math.max(0, Math.round(seconds * 1000));
  }

  // Shared formatter: milliseconds -> "MM:SS"
  private formatTimestampMs(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private msToSeconds(ms: number): number {
    return Math.max(0, Math.floor(ms / 1000));
  }

  private getSortedChunks(pageId: string): SerializedTranscriptionChunk[] {
    const chunks = this.getPageTranscription(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return [];
    return [...chunks].sort((a, b) => a.timestampMs - b.timestampMs);
  }

  private composeIncrementalSummaryText(
    chunks: SerializedSummaryChunk[] | undefined | null,
    upToMs?: number
  ): string | null {
    if (!Array.isArray(chunks) || chunks.length === 0) return null;
    const filtered = upToMs
      ? chunks.filter(
          c => Number.isFinite(c.timestampMs) && c.timestampMs <= upToMs
        )
      : chunks;
    const list = filtered.sort((a, b) => a.timestampMs - b.timestampMs);
    if (list.length === 0) return null;
    const result = list
      .map(c => {
        const timestamp = this.formatTimestampMs(c.timestampMs);
        const prefix = c.speaker
          ? `- [${timestamp}] ${c.speaker}: `
          : `- [${timestamp}] `;
        return prefix + c.text;
      })
      .join('\n');
    return result.trim().length > 0 ? result : null;
  }

  fetchSummaryUpToTsMs(pageId: string, tsMs: number): string | null {
    const raw = this.blobStorage.getPageSummarySync(pageId);
    if (!Array.isArray(raw) || raw.length === 0) return null;

    const upTo = Number.isFinite(tsMs) ? Math.max(0, Math.floor(tsMs)) : 0;

    const valid: SerializedSummaryChunk[] = [];
    for (const c of raw) {
      const parsed = SummaryChunkSchema.safeParse(c);
      if (parsed.success && Number.isFinite(parsed.data.timestampMs)) {
        valid.push(parsed.data);
      }
    }

    const composed = this.composeIncrementalSummaryText(valid, upTo);

    return composed;
  }

  getPageSummaryOrTranscript(pageId: string): string {
    const summaryChunks = this.blobStorage.getPageSummarySync(pageId);
    const fullSummary = this.composeIncrementalSummaryText(summaryChunks);

    if (typeof fullSummary === 'string' && fullSummary.trim().length > 0) {
      const lastSummaryTsMs = this.getLastProcessedTsMs(pageId);
      const transcriptChunks = this.getPageTranscription(pageId);
      const newerChunks = transcriptChunks.filter(
        c => c.timestampMs > lastSummaryTsMs
      );

      if (newerChunks.length === 0) {
        return fullSummary;
      }

      const tailTranscript = [...newerChunks]
        .sort((a, b) => a.timestampMs - b.timestampMs)
        .map(c => {
          const timestamp = this.formatTimestampMs(c.timestampMs);
          const prefix = c.speaker
            ? `[${timestamp}] ${c.speaker}: `
            : `[${timestamp}] `;
          return prefix + c.text;
        })
        .join('\n');

      return fullSummary + '\n\n' + tailTranscript;
    }

    return this.getPageTranscriptWithTimestamps(pageId);
  }

  appendIncrementalSummary(
    pageId: string,
    toTsMs: number,
    items: Array<{ timestamp: string; text: string; speaker?: string }>
  ): string | null {
    const existing = this.blobStorage.getPageSummarySync(pageId);
    const lastItem = existing.length > 0 ? existing[existing.length - 1] : null;
    if (lastItem && lastItem.timestampMs >= toTsMs) return null;

    let counter = 0;
    for (const it of items) {
      const chunk: SerializedSummaryChunk = {
        id: `sum-${Date.now()}-${counter++}`,
        text: it.text,
        timestampMs: this.parseTimestampToMs(it.timestamp),
        isFinal: true,
        ...(it.speaker ? { speaker: it.speaker } : {}),
      };
      this.blobStorage.addPageSummaryChunk(pageId, chunk);
    }

    return `sum-batch-${Date.now()}`;
  }

  getLastProcessedTsMs(pageId: string): number {
    const chunks = this.blobStorage.getPageSummarySync(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return 0;
    const lastChunk = chunks[chunks.length - 1];
    return lastChunk && Number.isFinite(lastChunk.timestampMs)
      ? lastChunk.timestampMs
      : 0;
  }

  getPageLatestTimestampMs(pageId: string): number {
    const chunks = this.getPageTranscription(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return 0;

    let maxTimestampMs = 0;
    for (const chunk of chunks) {
      if (!Number.isFinite(chunk.timestampMs)) continue;
      maxTimestampMs = Math.max(maxTimestampMs, Math.floor(chunk.timestampMs));
    }

    return maxTimestampMs;
  }

  getPageLatestTimestampSec(pageId: string): number {
    return this.msToSeconds(this.getPageLatestTimestampMs(pageId));
  }

  getNewChunksSinceMs(
    pageId: string,
    sinceTsMs: number
  ): Array<{ id: string; tsMs: number; text: string; speaker?: string }> {
    const chunks = this.getPageTranscription(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return [];
    const mapped = chunks.map(c => ({
      id: c.id,
      tsMs: c.timestampMs,
      text: c.text,
      speaker: c.speaker,
    }));
    return mapped
      .filter(c => c.tsMs > sinceTsMs)
      .sort((a, b) => a.tsMs - b.tsMs);
  }

  getClosestChunkIndexByMs(pageId: string, timestampMs: number): number {
    const chunks = this.getPageTranscription(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return -1;
    const target = Math.max(0, Math.floor(timestampMs));
    const withTs = chunks
      .map((c, idx) => ({ idx, ts: c.timestampMs }))
      .sort((a, b) => a.ts - b.ts);
    // Binary search nearest index
    let lo = 0,
      hi = withTs.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const midItem = withTs[mid];
      if (!midItem) break;
      const val = midItem.ts;
      if (val === target) return mid;
      if (val < target) lo = mid + 1;
      else hi = mid - 1;
    }
    const candidates: number[] = [];
    if (hi >= 0) candidates.push(hi);
    if (lo < withTs.length) candidates.push(lo);
    if (candidates.length === 0) return -1;
    const firstCandidate = candidates[0];
    if (firstCandidate === undefined) return -1;
    let bestIdx = firstCandidate;
    const bestItem = withTs[bestIdx];
    if (!bestItem) return -1;
    let bestDiff = Math.abs(bestItem.ts - target);
    for (const i of candidates) {
      const item = withTs[i];
      if (!item) continue;
      const d = Math.abs(item.ts - target);
      if (d < bestDiff) {
        bestIdx = i;
        bestDiff = d;
      }
    }
    return bestIdx;
  }

  getChunkTsMsAtIndex(pageId: string, index: number): number | null {
    const chunks = this.getPageTranscription(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return null;
    const sorted = [...chunks].map(c => c.timestampMs).sort((a, b) => a - b);
    if (index < 0 || index >= sorted.length) return null;
    return sorted[index] ?? null;
  }

  getPageTranscriptForUI(pageId: string): TranscriptChunkForUI[] {
    const chunks = this.getSortedChunks(pageId);
    if (!Array.isArray(chunks) || chunks.length === 0) return [];

    let userEmail: string | undefined;
    let userAvatar: string | undefined;
    try {
      const userInfoResult = AIProvider.userInfo;
      const userInfo =
        userInfoResult instanceof Promise ? undefined : userInfoResult;
      userEmail = userInfo?.email;
      userAvatar = userInfo?.avatarUrl || undefined;
    } catch {}

    const attendees = this.propertiesAdapter.getPageAttendees(pageId);

    return chunks.map(c => {
      let speakerAvatar: string | undefined;
      if (c.speaker && c.speaker.includes('@')) {
        if (c.speaker === userEmail && userAvatar) {
          speakerAvatar = userAvatar;
        } else {
          const attendee = attendees?.find(a => a.email === c.speaker);
          speakerAvatar = attendee?.avatar || undefined;
        }
      }
      return {
        text: c.text,
        timestamp: this.msToSeconds(c.timestampMs),
        speaker: c.speaker,
        speakerAvatar,
      };
    });
  }
}
